/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Typography, Stack, Box, Tooltip, Zoom, ButtonBase, TextField, InputAdornment } from '@mui/material';
import { CustomAvatar } from '../../../components/custom-avatar';
import { Iconify } from '../../../components';
import { useSelector, dispatch } from '../../../redux/store';
import { getActivities } from '../../../redux/slices/activity';
import capitalizeFirstLetter from '../../../utils/capitalizedFunc';
import { GetPracticeAvatar } from '../../../utils/createAvatar';

// ── Verb → icon + colour ─────────────────────────────────────────────────
// NOTE: hex literals (not palette tokens) — colours flow through `${color}1F`
// interpolations for soft-tinted dot backgrounds; theme tokens like
// `success.main` won't resolve when concatenated as substrings.
const VERB_CONFIG = {
    created: { icon: 'solar:user-plus-bold', color: '#00A76F' },
    treatment_added: { icon: 'solar:teeth-bold', color: '#078DEE' },
    treatment_started: { icon: 'solar:teeth-bold', color: '#00A76F' },
    lead_treatment_status_transitioned: { icon: 'eva:swap-fill', color: '#FFAB00' },
    note_added: { icon: 'solar:pen-bold', color: '#FF5630' },
    message_sent: { icon: 'ic:round-email', color: '#078DEE' },
    message_failed: { icon: 'ic:round-email', color: '#FF5630' },
    whatsapp_message_sent: { icon: 'ic:baseline-whatsapp', color: '#25D366' },
    whatsapp_message_failed: { icon: 'ic:baseline-whatsapp', color: '#FF5630' },
    appointment_added: { icon: 'solar:calendar-bold', color: '#5119B7' },
    appointment_booked: { icon: 'solar:calendar-bold', color: '#5119B7' },
    appointment_attended: { icon: 'eva:checkmark-circle-2-fill', color: '#00A76F' },
    appointment_failed: { icon: 'eva:close-circle-fill', color: '#FF5630' },
    appointment_fta: { icon: 'eva:close-circle-fill', color: '#FF5630' },
    appointment_cancelled: { icon: 'eva:close-circle-fill', color: '#919EAB' },
    appointment_rescheduled: { icon: 'solar:calendar-bold', color: '#FFAB00' },
    lead_archived: { icon: 'mdi:archive-outline', color: '#919EAB' },
    lead_restored: { icon: 'mdi:archive-arrow-up-outline', color: '#078DEE' },
    lead_payment_initiated: { icon: 'solar:wallet-money-bold', color: '#00A76F' },
    stripe_checkout_session_created: { icon: 'solar:wallet-money-bold', color: '#078DEE' },
    stripe_checkout_session_paid: { icon: 'solar:wallet-money-bold', color: '#00A76F' },
    stripe_refund_successful: { icon: 'solar:wallet-money-bold', color: '#FFAB00' },
    stripe_refund_failed: { icon: 'solar:wallet-money-bold', color: '#FF5630' },
    // Task checklist verbs (Tasks V2 — #18)
    task_checklist_email_sent:          { icon: 'ic:round-email',                   color: '#078DEE' },
    task_checklist_whatsapp_sent:       { icon: 'ic:baseline-whatsapp',              color: '#25D366' },
    task_checklist_next_action:         { icon: 'mdi:arrow-right-circle-outline',    color: '#5119B7' },
    task_checklist_status_changed:      { icon: 'eva:swap-fill',                     color: '#FFAB00' },
    task_checklist_attendance_recorded: { icon: 'eva:checkmark-circle-2-fill',       color: '#00A76F' },
    task_checklist_fta_recorded:        { icon: 'eva:close-circle-fill',             color: '#FF5630' },
    task_checklist_treatment_started:   { icon: 'solar:teeth-bold',                  color: '#00A76F' },
    task_checklist_treatment_completed: { icon: 'solar:teeth-bold',                  color: '#5119B7' },
    task_due_date_changed:              { icon: 'solar:calendar-mark-bold',          color: '#FFAB00' },
    default: { icon: 'solar:bell-bold', color: '#919EAB' },
};

const getActivityIcon = (act) => {
    const verb = act?.verb;
    if (verb === 'note_added') {
        const noteType = act?.action_object?.note;
        const via = act?.action_object?.contacted_via;
        if (noteType === 'UNABLE_TO_CONTACT') return { icon: 'solar:phone-calling-bold', color: '#FF5630' };
        if (via === 'PHONE') return { icon: 'fluent:call-20-filled', color: '#00A76F' };
        if (via === 'WHATSAPP') return { icon: 'ic:baseline-whatsapp', color: '#25D366' };
        if (via === 'EMAIL') return { icon: 'ic:round-email', color: '#078DEE' };
        if (via === 'SMS') return { icon: 'bi:chat-left-dots-fill', color: '#7635DC' };
        if (noteType === 'FOLLOW_UP') return { icon: 'mdi:clock-time-three-outline', color: '#FFAB00' };
        if (noteType === 'AWAITING_RESPONSE') return { icon: 'mdi:clock-time-three-outline', color: '#078DEE' };
        return { icon: 'solar:pen-bold', color: '#637381' };
    }
    if (verb === 'message_sent') {
        const ao = act?.action_object;
        const isAuto = ao?.message_group === 'SYSTEM' || ao?.message_category === 'SEQUENCE_MESSAGE';
        if (isAuto) return { icon: 'mdi:email-fast-outline', color: '#919EAB' };
        return { icon: 'ic:round-email', color: '#078DEE' };
    }
    return VERB_CONFIG[verb] || VERB_CONFIG.default;
};

// ── AI-note parsing ──────────────────────────────────────────────────────
const parseAINote = (noteContent) => {
    if (!noteContent) return null;
    try {
        const parsed = JSON.parse(noteContent);
        if (parsed && parsed.AI_NOTES === true) return parsed;
    } catch (e) { /* not JSON */ }
    return null;
};

const getNoteDisplayText = (noteContent) => {
    const aiData = parseAINote(noteContent);
    if (aiData) return '🤖 AI Call Summary - Click to view in lead details';
    return capitalizeFirstLetter(noteContent);
};

// ── Engagement aggregation ───────────────────────────────────────────────
const ENGAGEMENT_LABELS = {
    delivered: 'Delivered',
    opened: 'Opens',
    clicked: 'Clicks',
    bounced: 'Bounced',
    unsubscribed: 'Unsubscribed',
};

const getEngagementCounts = (action) => {
    if (action?.verb !== 'message_sent' && action?.verb !== 'message_failed') return null;
    if (action?.action_object?.type !== 'EMAIL') return null;
    if (action?.action_object?.message_category === 'NOTIFICATION') return null;
    const counts = {};
    (action?.action_object?.sent_messages || []).forEach((sm) => {
        (sm?.event_counts || []).forEach((ec) => {
            if (ec?.event && ec?.count) counts[ec.event] = (counts[ec.event] || 0) + ec.count;
        });
    });
    return Object.keys(counts).length > 0 ? counts : null;
};

const getEmailStatus = (act) => {
    if (act?.verb !== 'message_sent' && act?.verb !== 'message_failed') return null;
    if (act?.action_object?.type !== 'EMAIL') return null;
    if (act?.action_object?.message_category === 'NOTIFICATION') return null;
    if (act.verb === 'message_failed') return { label: 'Failed', color: '#FF5630', icon: 'eva:close-circle-fill' };
    const counts = getEngagementCounts(act);
    if (counts?.bounced) return { label: 'Bounced', color: '#FF5630', icon: 'solar:close-circle-bold', counts };
    if (counts?.unsubscribed) return { label: 'Unsubscribed', color: '#919EAB', icon: 'mdi:bell-off-outline', counts };
    if (counts?.clicked) return { label: counts.clicked > 1 ? `${counts.clicked} Clicks` : 'Clicked', color: '#00A76F', icon: 'solar:cursor-bold', counts };
    if (counts?.opened) return { label: counts.opened > 1 ? `${counts.opened} Opens` : 'Opened', color: '#3b82f6', icon: 'solar:eye-bold', counts };
    if (counts?.delivered) return { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill', counts };
    const isAuto = act?.action_object?.message_group === 'SYSTEM' || act?.action_object?.message_category === 'SEQUENCE_MESSAGE';
    if (isAuto) return { label: 'Auto', color: '#C4CDD5', icon: 'mdi:robot-outline' };
    return { label: 'Sent', color: '#919EAB', icon: 'mdi:check' };
};

const WHATSAPP_STATUS = {
    read: { label: 'Read', color: '#078DEE', icon: 'eva:done-all-fill' },
    delivered: { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill' },
    sent: { label: 'Sent', color: '#C4CDD5', icon: 'mdi:check' },
    failed: { label: 'Failed', color: '#FF5630', icon: 'eva:close-fill' },
};

const getWhatsAppStatus = (act) => {
    if (act?.verb === 'whatsapp_message_failed') return { label: 'Failed', color: '#FF5630', icon: 'eva:close-circle-fill' };
    if (act?.verb !== 'whatsapp_message_sent') return null;
    return WHATSAPP_STATUS[act?.action_object?.status || 'sent'] || WHATSAPP_STATUS.sent;
};

const buildStatusTooltip = (status) => {
    if (!status?.counts) return status?.label || '';
    return Object.entries(ENGAGEMENT_LABELS)
        .filter(([k]) => status.counts[k])
        .map(([k, label]) => `${status.counts[k]} ${label}`)
        .join(' · ');
};

// ── Filter pills ─────────────────────────────────────────────────────────
const categorizeActivity = (activity) => {
    const verb = activity?.verb;
    const note = activity?.action_object?.note;
    const via = activity?.action_object?.contacted_via;
    const buckets = [];
    if (verb === 'note_added') {
        if (via === 'PHONE') buckets.push('calls');
        else if (via === 'EMAIL') buckets.push('emails');
        else if (via === 'WHATSAPP') buckets.push('whatsapp');
        else if (via === 'SMS') buckets.push('sms');
        if (note === 'UNABLE_TO_CONTACT') buckets.push('failed');
    } else if (verb === 'message_sent') buckets.push('emails');
    else if (verb === 'message_failed') buckets.push('emails', 'failed');
    else if (verb === 'whatsapp_message_sent') buckets.push('whatsapp');
    else if (verb === 'whatsapp_message_failed') buckets.push('whatsapp', 'failed');
    else if (verb === 'appointment_added' || verb === 'appointment_rescheduled' || verb === 'appointment_cancelled') {
        buckets.push('appointments');
    }
    return buckets.length > 0 ? buckets : ['other'];
};

const SUMMARY_PILLS = [
    { key: 'all', label: 'All', icon: 'mdi:format-list-bulleted', color: '#637381' },
    { key: 'calls', label: 'Calls', icon: 'fluent:call-20-filled', color: '#00A76F' },
    { key: 'emails', label: 'Emails', icon: 'eva:email-fill', color: '#1890FF' },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'ri:whatsapp-fill', color: '#25D366' },
    { key: 'sms', label: 'SMS', icon: 'bi:chat-left-dots-fill', color: '#7635DC' },
    { key: 'appointments', label: 'Appointments', icon: 'mdi:calendar-check', color: '#FF6C40' },
    { key: 'failed', label: 'Failed', icon: 'mdi:alert-circle-outline', color: '#FF5630' },
];

const INITIAL_FETCH_LIMIT = 100;
const SCROLL_FETCH_BATCH = 25;
const TS_FORMATS = [
    'DD-MM-YYYY HH:mm:ss.SSSSSSZ',
    'DD-MM-YYYY HH:mm:ss.SSSZ',
    'DD-MM-YYYY HH:mm:ssZ',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY',
    moment.ISO_8601,
];

// Smart relative time — uses fuzzy text for recent events, absolute for older.
const formatActivityTime = (timestamp) => {
    const m = moment(timestamp, TS_FORMATS);
    if (!m.isValid()) return '';
    const now = moment();
    const diffHours = now.diff(m, 'hours');
    const diffDays = now.diff(m.clone().startOf('day'), 'days');
    if (diffHours < 24 && diffDays === 0) return m.fromNow(); // "2 hours ago"
    if (diffDays === 1) return `Yesterday at ${m.format('h:mm a')}`;
    if (diffDays > 1 && diffDays <= 6) return `${m.format('dddd')} at ${m.format('h:mm a')}`; // "Wednesday at 3:14 pm"
    if (m.year() === now.year()) return m.format('DD MMM · h:mm a');
    return m.format('DD MMM YYYY · h:mm a');
};

// Outbound contact = something we sent OUT to the lead.
const isOutboundContact = (act) => {
    const v = act?.verb;
    if (v === 'message_sent' || v === 'whatsapp_message_sent') return true;
    if (v === 'note_added') {
        const noteType = act?.action_object?.note;
        return noteType === 'FOLLOW_UP' || noteType === 'AWAITING_RESPONSE';
    }
    return false;
};

// Searches title text, actor name, additional notes, and email subject.
const matchesSearch = (act, query) => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
        act?.data?.data?.message,
        act?.actor?.first_name,
        act?.actor?.last_name,
        act?.action_object?.additional_notes,
        act?.action_object?.subject,
        act?.action_object?.body,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
};

// Group activities into Today / Yesterday / This week / Earlier buckets.
const groupByDate = (items) => {
    const today = moment().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    const weekStart = today.clone().subtract(6, 'days'); // last 7 days incl today
    const groups = [
        { key: 'today', label: 'Today', items: [] },
        { key: 'yesterday', label: 'Yesterday', items: [] },
        { key: 'thisWeek', label: 'This week', items: [] },
        { key: 'earlier', label: 'Earlier', items: [] },
    ];
    items.forEach((item) => {
        const ts = moment(item.timestamp, TS_FORMATS);
        if (!ts.isValid()) { groups[3].items.push(item); return; }
        if (ts.isSameOrAfter(today)) groups[0].items.push(item);
        else if (ts.isSameOrAfter(yesterday)) groups[1].items.push(item);
        else if (ts.isSameOrAfter(weekStart)) groups[2].items.push(item);
        else groups[3].items.push(item);
    });
    return groups.filter((g) => g.items.length > 0);
};

const Activities = (data) => {
    const { practiceTreatment, practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { activityList } = useSelector((state) => state.activity);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (data?.leadId) {
            dispatch(getActivities(practiceId, data?.leadId, INITIAL_FETCH_LIMIT, 0));
            setFilter('all');
            setSearch('');
            setExpandedId(null);
        }
    }, [data?.leadId]);

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
        if (bottom && Array.isArray(activityList?.results) && activityList?.count > activityList?.results?.length) {
            dispatch(getActivities(practiceId, data?.leadId, SCROLL_FETCH_BATCH, activityList?.results?.length));
        }
    };

    const counts = useMemo(() => {
        const result = { all: 0, calls: 0, emails: 0, whatsapp: 0, sms: 0, appointments: 0, failed: 0 };
        const items = activityList?.results || [];
        result.all = items.length;
        items.forEach((item) => {
            categorizeActivity(item).forEach((b) => {
                if (b in result) result[b] += 1;
            });
        });
        return result;
    }, [activityList?.results]);

    const filteredItems = useMemo(() => {
        const items = activityList?.results || [];
        return items.filter((item) =>
            (filter === 'all' || categorizeActivity(item).includes(filter))
            && matchesSearch(item, search)
        );
    }, [activityList?.results, filter, search]);

    const groupedItems = useMemo(() => groupByDate(filteredItems), [filteredItems]);

    // Most recent outbound contact id — used to mark the row with a subtle ring.
    const latestOutboundId = useMemo(() => {
        const items = activityList?.results || [];
        const found = items.find((it) => isOutboundContact(it));
        return found ? (found.id || null) : null;
    }, [activityList?.results]);

    // Stats banner — total + last contact relative time.
    const lastContactText = useMemo(() => {
        const items = activityList?.results || [];
        const last = items.find((it) => isOutboundContact(it));
        if (!last) return null;
        const m = moment(last.timestamp, TS_FORMATS);
        if (!m.isValid()) return null;
        return m.fromNow();
    }, [activityList?.results]);

    const totalLoaded = activityList?.results?.length || 0;
    const totalAvailable = activityList?.count || 0;
    const hasMore = totalAvailable > totalLoaded;

    return (
        <Box
            onScroll={handleScroll}
            sx={{
                maxHeight: data?.type === 'contactLeadPopup' ? 'calc(100vh - 220px)' : '880px',
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
            }}
        >
            {/* Sticky header — search + filter pills */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                bgcolor: 'background.paper',
                px: 2.5,
                pt: 2,
                pb: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <TextField
                    fullWidth
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search messages, notes, people…"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" sx={{ width: 18, height: 18, color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <ButtonBase onClick={() => setSearch('')} sx={{ p: 0.25, borderRadius: '50%' }}>
                                    <Iconify icon="eva:close-fill" sx={{ width: 16, height: 16, color: 'text.disabled' }} />
                                </ButtonBase>
                            </InputAdornment>
                        ) : null,
                        sx: { fontSize: '0.825rem', borderRadius: 1 },
                    }}
                    sx={{ mb: 1.25 }}
                />
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    {SUMMARY_PILLS.map((pill) => {
                        const count = counts[pill.key] ?? 0;
                        const isActive = filter === pill.key;
                        const isDisabled = pill.key !== 'all' && count === 0;
                        return (
                            <ButtonBase
                                key={pill.key}
                                disabled={isDisabled}
                                onClick={() => setFilter(pill.key)}
                                sx={{
                                    px: 1.25, py: 0.5, borderRadius: 1,
                                    bgcolor: isActive ? pill.color : 'background.paper',
                                    border: '1px solid',
                                    borderColor: isActive ? pill.color : 'divider',
                                    color: isActive ? 'common.white' : 'text.primary',
                                    opacity: isDisabled ? 0.4 : 1,
                                    transition: 'all 0.15s',
                                    '&:hover:not(:disabled)': { bgcolor: isActive ? pill.color : 'action.hover' },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Iconify icon={pill.icon} sx={{ width: 14, height: 14, color: isActive ? 'common.white' : pill.color }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{pill.label}</Typography>
                                    <Box sx={{
                                        px: 0.75, py: 0.1, borderRadius: 4,
                                        bgcolor: isActive ? 'rgba(255,255,255,0.25)' : 'action.hover',
                                        fontSize: '0.7rem', fontWeight: 700, minWidth: 18, textAlign: 'center',
                                    }}>{count}</Box>
                                </Stack>
                            </ButtonBase>
                        );
                    })}
                </Stack>
            </Box>

            {/* Stats banner */}
            {totalLoaded > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                        px: 2.5,
                        py: 1.25,
                        bgcolor: 'background.neutral',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        flexWrap: 'wrap',
                    }}
                >
                    <Iconify icon="solar:graph-up-bold" sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                    {(() => {
                        // Pluralise against whatever count we actually display, otherwise
                        // we render "1 activities" when totalAvailable=0 but totalLoaded=1.
                        const shown = totalAvailable || totalLoaded;
                        return (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
                                {shown} {shown === 1 ? 'activity' : 'activities'}
                            </Typography>
                        );
                    })()}
                    {lastContactText && (
                        <>
                            <Box sx={{ width: 3, height: 3, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                last contacted <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{lastContactText}</Box>
                            </Typography>
                        </>
                    )}
                    {(filter !== 'all' || search) && (
                        <>
                            <Box sx={{ width: 3, height: 3, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                showing <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{filteredItems.length}</Box>
                            </Typography>
                        </>
                    )}
                </Stack>
            )}

            {/* Feed */}
            <Box sx={{ px: 2.5, py: 2 }}>
                {filteredItems.length === 0 && totalLoaded > 0 && (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                        <Iconify icon="eva:inbox-outline" sx={{ width: 32, height: 32, color: 'text.disabled', mb: 1 }} />
                        <Typography sx={{ fontSize: '0.825rem', color: 'text.secondary', fontWeight: 500 }}>
                            No matches{search ? ` for "${search}"` : ''}
                            {hasMore ? ' — scroll for more' : ''}
                        </Typography>
                    </Box>
                )}

                {groupedItems.map((group, groupIdx) => (
                    <Box key={group.key} sx={{ mt: groupIdx === 0 ? 0 : 2.5 }}>
                        {/* Date header */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.25}
                            sx={{ mb: 1.5 }}
                        >
                            <Typography sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}>
                                {group.label}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600 }}>
                                {group.items.length}
                            </Typography>
                        </Stack>

                        {/* Group rows */}
                        {group.items.map((act, index) => {
                            const isLast = index === group.items.length - 1;
                            const { icon: dotIcon, color: dotColor } = getActivityIcon(act);
                            const status = getEmailStatus(act) || getWhatsAppStatus(act);
                            const isExpandable =
                                act.verb === 'whatsapp_message_sent'
                                || act.verb === 'whatsapp_message_failed'
                                || act.verb === 'message_sent'
                                || act.verb === 'message_failed';
                            const rowKey = act.id || `${group.key}-${index}`;
                            const isExpanded = expandedId === rowKey;
                            const isLatest = act.id && act.id === latestOutboundId;
                            const rawTitle = act?.data?.data?.message || '';
                            const title = rawTitle === 'Awaiting Response.' ? 'Awaiting Reply.' : capitalizeFirstLetter(rawTitle);
                            const dateStr = formatActivityTime(act.timestamp);
                            const actorName = `${act?.actor?.first_name || ''} ${act?.actor?.last_name || ''}`.trim();
                            const additionalNotes = act?.action_object?.additional_notes;

                            return (
                                <Box
                                    key={rowKey}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        pb: isLast ? 0 : 2.25,
                                    }}
                                >
                                    {!isLast && (
                                        <Box sx={{
                                            position: 'absolute',
                                            left: 15,
                                            top: 34,
                                            bottom: 0,
                                            width: '1px',
                                            bgcolor: 'rgba(145,158,171,0.16)',
                                        }} />
                                    )}

                                    {/* Dot */}
                                    <Box sx={{
                                        position: 'relative',
                                        width: 32,
                                        height: 32,
                                        flexShrink: 0,
                                        zIndex: 1,
                                        mt: '1px',
                                    }}>
                                        {isLatest && (
                                            <Tooltip title="Most recent contact" placement="top">
                                                <Box sx={{
                                                    position: 'absolute',
                                                    inset: -3,
                                                    borderRadius: '50%',
                                                    border: '2px solid',
                                                    borderColor: 'success.main',
                                                    opacity: 0.35,
                                                }} />
                                            </Tooltip>
                                        )}
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            bgcolor: `${dotColor}1F`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <Iconify icon={dotIcon} sx={{ width: 16, height: 16, color: dotColor }} />
                                        </Box>
                                    </Box>

                                    {/* Content */}
                                    <Box sx={{ flex: 1, minWidth: 0, ml: 1.75, pt: 0.5 }}>
                                        <Stack
                                            direction="row"
                                            alignItems="flex-start"
                                            spacing={1.5}
                                            role={isExpandable ? 'button' : undefined}
                                            tabIndex={isExpandable ? 0 : undefined}
                                            aria-expanded={isExpandable ? isExpanded : undefined}
                                            sx={{
                                                cursor: isExpandable ? 'pointer' : 'default',
                                                outline: 'none',
                                                '&:focus-visible': isExpandable ? {
                                                    boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                                    borderRadius: 1,
                                                } : {},
                                                '&:hover .timeline-title': isExpandable ? { color: 'primary.main' } : {},
                                            }}
                                            onClick={isExpandable ? () => setExpandedId(isExpanded ? null : rowKey) : undefined}
                                            onKeyDown={isExpandable ? (e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setExpandedId(isExpanded ? null : rowKey);
                                                }
                                            } : undefined}
                                        >
                                            <Typography
                                                className="timeline-title"
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 500,
                                                    color: 'text.primary',
                                                    lineHeight: 1.4,
                                                    transition: 'color 0.15s',
                                                    ...(isExpandable && !isExpanded && {
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }),
                                                    ...(isExpandable && isExpanded && {
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }),
                                                }}
                                            >
                                                {title || '—'}
                                            </Typography>

                                            {status && (
                                                <Tooltip title={buildStatusTooltip(status)} placement="left" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                                                    <Box sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        px: 0.875,
                                                        py: 0.25,
                                                        borderRadius: 0.75,
                                                        bgcolor: `${status.color}14`,
                                                        flexShrink: 0,
                                                        mt: '1px',
                                                        cursor: 'default',
                                                    }}>
                                                        <Iconify icon={status.icon} sx={{ width: 13, height: 13, color: status.color }} />
                                                        <Typography sx={{
                                                            fontSize: '0.6875rem',
                                                            fontWeight: 600,
                                                            color: status.color,
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap',
                                                            letterSpacing: 0.1,
                                                        }}>
                                                            {status.label}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Stack>

                                        <Stack direction="row" alignItems="center" spacing={0.875} sx={{ mt: 0.625, flexWrap: 'wrap', rowGap: 0.5 }}>
                                            {actorName && (
                                                <Stack direction="row" alignItems="center" spacing={0.625}>
                                                    <CustomAvatar
                                                        src={act?.actor?.avatar || GetPracticeAvatar(practiceTreatment, practiceId)[0]?.logo}
                                                        name={act?.actor?.first_name}
                                                        sx={{ width: 18, height: 18, fontSize: '0.6rem' }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 500 }}>
                                                        {actorName}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {actorName && dateStr && (
                                                <Box sx={{ width: 2.5, height: 2.5, bgcolor: 'text.disabled', borderRadius: '50%', flexShrink: 0 }} />
                                            )}
                                            {dateStr && (
                                                <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', fontWeight: 500 }}>
                                                    {dateStr}
                                                </Typography>
                                            )}
                                            {additionalNotes && (
                                                <>
                                                    <Box sx={{ width: 2.5, height: 2.5, bgcolor: 'text.disabled', borderRadius: '50%', flexShrink: 0 }} />
                                                    <Tooltip
                                                        title={getNoteDisplayText(additionalNotes)}
                                                        placement="bottom"
                                                        TransitionComponent={Zoom}
                                                        TransitionProps={{ timeout: 200 }}
                                                    >
                                                        <Typography
                                                            tabIndex={0}
                                                            aria-label={`Note: ${getNoteDisplayText(additionalNotes)}`}
                                                            sx={{
                                                                fontSize: '0.6875rem',
                                                                color: 'text.secondary',
                                                                fontStyle: 'italic',
                                                                fontWeight: 500,
                                                                maxWidth: 200,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                outline: 'none',
                                                                '&:focus-visible': {
                                                                    boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                                                    borderRadius: '4px',
                                                                },
                                                            }}
                                                        >
                                                            {parseAINote(additionalNotes) ? '🤖 AI Summary' : `“${capitalizeFirstLetter(additionalNotes).slice(0, 60)}${additionalNotes.length > 60 ? '…' : ''}”`}
                                                        </Typography>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ))}

                {hasMore && (
                    <Box sx={{ textAlign: 'center', py: 1.5, mt: 1 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 500 }}>
                            Showing {totalLoaded} of {totalAvailable} — scroll to load more
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Activities;
